import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";
import { Badge, Icon, IconButton, Input, Logo, Toast } from "../ds/DS";
import { ClientCreateDialog } from "./pages/EditDialogs";
import {
  usePortalData,
  isOpen,
  type VClient,
  type VProject,
  type VTicket,
  type VMilestone,
  type VChangeRequest,
  type VCredential,
  type VDocument,
  type VTask,
  type VActivity,
  type VRelease,
  type VReleaseChange,
} from "./data";
import { Overview } from "./pages/Overview";
import { ProjectsPage } from "./pages/ProjectsPage";
import { Tickets } from "./pages/Tickets";
import { Milestones } from "./pages/Milestones";
import { Releases } from "./pages/Releases";
import { Changes } from "./pages/Changes";
import { Tasks } from "./pages/Tasks";
import { ToDo } from "./pages/ToDo";
import { buildTodo, releaseAwaitingSignoff } from "./todo";
import { Vault } from "./pages/Vault";
import { Documents } from "./pages/Documents";
import { Activity } from "./pages/Activity";
import { Team } from "./pages/Team";
import { Placeholder } from "./pages/Placeholder";
import "./portal.css";

export type PortalRole = "client" | "admin";

interface NavDef {
  id: string;
  label: string;
  icon: string;
  level: "both" | "client";
  // Client portal (O5): six pages only, with their own labels and icons.
  client?: { label: string; icon: string };
}

const NAV: NavDef[] = [
  {
    id: "dashboard",
    label: "Overview",
    icon: "layout-dashboard",
    level: "both",
    client: { label: "Home", icon: "house" },
  },
  { id: "projects", label: "Projects", icon: "folder-kanban", level: "client" },
  {
    id: "tickets",
    label: "Tickets",
    icon: "message-square",
    level: "both",
    client: { label: "Support", icon: "message-square" },
  },
  {
    id: "tasks",
    label: "Tasks",
    icon: "clipboard-check",
    level: "both",
    client: { label: "To do", icon: "list-checks" },
  },
  { id: "milestones", label: "Milestones", icon: "flag", level: "both" },
  {
    id: "releases",
    label: "Releases",
    icon: "rocket",
    level: "both",
    client: { label: "Releases", icon: "rocket" },
  },
  { id: "changes", label: "Change requests", icon: "git-branch", level: "both" },
  {
    id: "vault",
    label: "Credentials",
    icon: "shield-check",
    level: "both",
    client: { label: "Logins", icon: "key-round" },
  },
  {
    id: "files",
    label: "Documents",
    icon: "folder",
    level: "both",
    client: { label: "Files", icon: "folder" },
  },
  { id: "activity", label: "Activity", icon: "activity", level: "both" },
  { id: "team", label: "Team & access", icon: "users", level: "client" },
];

// The client sidebar, in order. Any other page is admin-only: a client who
// lands on one (stale state, a cross-page link) gets Home instead.
const CLIENT_NAV = ["dashboard", "tickets", "tasks", "releases", "files", "vault"];

export interface PageProps {
  role: PortalRole;
  isAdmin: boolean;
  client: VClient | undefined;
  clients: VClient[];
  projects: VProject[]; // this client's projects
  scopedProjects: VProject[]; // narrowed to pid when in a project
  tickets: VTicket[]; // this client's tickets
  milestones: VMilestone[]; // this client's milestones
  changeRequests: VChangeRequest[]; // this client's change requests
  credentials: VCredential[]; // this client's credentials
  documents: VDocument[]; // this client's documents
  tasks: VTask[]; // this client's tasks
  activity: VActivity[]; // this client's activity feed
  releases: VRelease[]; // this client's releases
  releaseChanges: VReleaseChange[]; // change entries of those releases
  crRatePence: number; // change-request rate, pence per hour ex VAT
  pid: string;
  me: { name: string; email: string };
  setPid: (id: string) => void;
  go: (page: string, clientId?: string) => void;
  enterProject: (id: string) => void;
  toast: (msg: string) => void;
  reload: () => void;
  projectName: (pid: string) => string;
  // Cross-page navigation to one item (e.g. a ticket's release, a release's
  // ticket). The target page reads `focus` and calls clearFocus().
  focus: { page: string; id: string } | null;
  openItem: (page: string, id: string) => void;
  clearFocus: () => void;
  // Extra breadcrumb segment for the current page (e.g. a release's name);
  // the page clears it when it unmounts.
  setCrumbTail: (s: string) => void;
}

export default function Portal() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const data = usePortalData(isAdmin);

  const role: PortalRole = isAdmin ? "admin" : "client";

  const [clientId, setClientId] = useState<string>("");
  const [pid, setPid] = useState<string>("all");
  const [requestedPage, setPage] = useState<string>("dashboard");
  // Route guard: clients only ever render the six client pages.
  const page = isAdmin || CLIENT_NAV.includes(requestedPage) ? requestedPage : "dashboard";
  const [toasts, setToasts] = useState<{ id: string; msg: string }[]>([]);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgQ, setOrgQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeQ, setScopeQ] = useState("");
  const [navOpen, setNavOpen] = useState(false); // mobile drawer
  const [clientCreateOpen, setClientCreateOpen] = useState(false);
  const [focus, setFocus] = useState<{ page: string; id: string } | null>(null);
  const [crumbTail, setCrumbTail] = useState("");

  // Once data loads, lock onto a valid, non-archived client.
  useEffect(() => {
    if (!data.clients.length) return;
    const current = data.clients.find((c) => c.id === clientId);
    if (!current || current.archived) {
      const next =
        data.clients.find((c) => !c.archived) ?? data.clients[0];
      if (next) {
        setClientId(next.id);
        setPid("all");
      }
    }
  }, [data.clients, clientId]);

  const toast = (msg: string) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => t.concat({ id, msg }));
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3600);
  };

  const client = data.clients.find((c) => c.id === clientId);
  const clientProjects = useMemo(
    () => data.projects.filter((p) => p.clientId === clientId),
    [data.projects, clientId],
  );
  const projectIds = useMemo(
    () => new Set(clientProjects.map((p) => p.id)),
    [clientProjects],
  );
  const clientTickets = useMemo(
    () => data.tickets.filter((t) => projectIds.has(t.projectId)),
    [data.tickets, projectIds],
  );
  const clientMilestones = useMemo(
    () => data.milestones.filter((m) => projectIds.has(m.projectId)),
    [data.milestones, projectIds],
  );
  const clientChangeRequests = useMemo(
    () => data.changeRequests.filter((cr) => projectIds.has(cr.projectId)),
    [data.changeRequests, projectIds],
  );
  const clientCredentials = useMemo(
    () => data.credentials.filter((cr) => cr.clientId === clientId),
    [data.credentials, clientId],
  );
  const clientDocuments = useMemo(
    () => data.documents.filter((d) => d.clientId === clientId),
    [data.documents, clientId],
  );
  const clientTasks = useMemo(
    () => data.tasks.filter((t) => t.clientId === clientId),
    [data.tasks, clientId],
  );
  // Activity carries its client, so account-level entries (vault, files,
  // team) show alongside project ones.
  const clientActivity = useMemo(
    () => data.activity.filter((a) => a.clientId === clientId),
    [data.activity, clientId],
  );
  const clientReleases = useMemo(
    () => data.releases.filter((r) => projectIds.has(r.projectId)),
    [data.releases, projectIds],
  );
  const clientReleaseIds = useMemo(
    () => new Set(clientReleases.map((r) => r.id)),
    [clientReleases],
  );
  const clientReleaseChanges = useMemo(
    () => data.releaseChanges.filter((ch) => clientReleaseIds.has(ch.releaseId)),
    [data.releaseChanges, clientReleaseIds],
  );

  const projectName = (id: string) =>
    id === "all"
      ? "All projects"
      : (clientProjects.find((p) => p.id === id)?.name ?? "Account-wide");

  const inProject = pid !== "all";
  const scopedProjects = inProject
    ? clientProjects.filter((p) => p.id === pid)
    : clientProjects;
  const scopedTickets = inProject
    ? clientTickets.filter((t) => t.projectId === pid)
    : clientTickets;
  const scopedReleases = inProject
    ? clientReleases.filter((r) => r.projectId === pid)
    : clientReleases;
  const scopedCRs = inProject
    ? clientChangeRequests.filter((cr) => cr.projectId === pid)
    : clientChangeRequests;
  // A project view scopes to that project; the all-projects view spans every
  // task for the client (account-level and all projects), matching the Tasks page.
  const scopedTasks = inProject
    ? clientTasks.filter((t) => t.projectId === pid)
    : clientTasks;

  const counts: Record<string, number> = {
    // Admin: tickets that need a reply; client: tickets waiting on them.
    tickets: scopedTickets.filter((t) =>
      isAdmin ? t.needsReply && isOpen(t) : t.status === "Waiting on client",
    ).length,
    changes: scopedCRs.filter((cr) => cr.state === "Awaiting approval").length,
    // Admin: everything in flight (with the client + ready to review); the
    // client's To do pill counts every item on their To do list.
    tasks: isAdmin
      ? scopedTasks.filter((t) => t.state === "Requested" || t.state === "Submitted").length
      : buildTodo({
          tickets: clientTickets,
          releases: clientReleases,
          milestones: clientMilestones,
          tasks: clientTasks,
          changeRequests: clientChangeRequests,
          projectName,
        }).length,
    projects: clientProjects.length,
    // Admin: releases in flight (Open + On sandbox); client: sign-offs pending.
    releases: scopedReleases.filter((r) =>
      isAdmin ? r.status === "Open" || r.status === "On sandbox" : releaseAwaitingSignoff(r),
    ).length,
  };

  const go = (p: string, c?: string) => {
    if (c) {
      setClientId(c);
      setPid("all");
    }
    setPage(p);
  };
  const openItem = (p: string, id: string) => {
    setFocus({ page: p, id });
    setPage(p);
  };
  const switchClient = (c: string) => {
    setClientId(c);
    setPid("all");
    setOrgOpen(false);
    setOrgQ("");
    setShowArchived(false);
    setScopeOpen(false);
    setScopeQ("");
  };
  const setClientArchived = async (c: VClient, archived: boolean) => {
    await api.patch(`/clients/${c.id}`, { archived });
    data.reload();
    toast(archived ? `${c.name} archived.` : `${c.name} restored.`);
  };
  const enterProject = (id: string) => {
    setPid(id);
    setScopeOpen(false);
    setScopeQ("");
    if (page === "projects" || page === "team") setPage("dashboard");
  };
  const exitProject = () => {
    setPid("all");
    setScopeOpen(false);
    setScopeQ("");
  };

  // The admin ticket inbox runs full-bleed under the top bar.
  const flush =
    isAdmin && page === "tickets" && !data.loading && !data.error;
  const navItems: { id: string; label: string; icon: string }[] = isAdmin
    ? NAV.filter((n) => (inProject ? n.level !== "client" : true))
    : CLIENT_NAV.flatMap((id) => {
        const n = NAV.find((x) => x.id === id);
        return n?.client ? [{ id, ...n.client }] : [];
      });
  const pageLabel = (id: string) => {
    const n = NAV.find((x) => x.id === id);
    return (isAdmin ? n?.label : (n?.client?.label ?? n?.label)) ?? id;
  };
  const me = { name: user?.name || "You", email: user?.email || "" };

  const pageProps: PageProps = {
    role,
    isAdmin,
    client,
    clients: data.clients,
    projects: clientProjects,
    scopedProjects,
    tickets: clientTickets,
    milestones: clientMilestones,
    changeRequests: clientChangeRequests,
    credentials: clientCredentials,
    documents: clientDocuments,
    tasks: clientTasks,
    activity: clientActivity,
    releases: clientReleases,
    releaseChanges: clientReleaseChanges,
    crRatePence: data.crRatePence,
    pid,
    me,
    setPid,
    go,
    enterProject,
    toast,
    reload: data.reload,
    projectName,
    focus,
    openItem,
    clearFocus: () => setFocus(null),
    setCrumbTail,
  };

  const renderPage = () => {
    if (data.loading) return <div className="empty">Loading…</div>;
    if (data.error) return <div className="empty">{data.error}</div>;
    switch (page) {
      case "dashboard":
        return <Overview {...pageProps} />;
      case "projects":
        return <ProjectsPage {...pageProps} />;
      case "tickets":
        return <Tickets {...pageProps} />;
      case "milestones":
        return <Milestones {...pageProps} />;
      case "releases":
        return <Releases {...pageProps} />;
      case "changes":
        return <Changes {...pageProps} />;
      case "tasks":
        return isAdmin ? <Tasks {...pageProps} /> : <ToDo {...pageProps} />;
      case "vault":
        return <Vault {...pageProps} />;
      case "files":
        return <Documents {...pageProps} />;
      case "activity":
        return <Activity {...pageProps} />;
      case "team":
        return <Team {...pageProps} />;
      default:
        return (
          <Placeholder title={pageLabel(page)} />
        );
    }
  };

  const initials = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const clientMatch = (c: VClient) =>
    c.name.toLowerCase().includes(orgQ.toLowerCase());

  const openCountFor = (cid: string) =>
    data.tickets.filter(
      (t) =>
        data.projects.some((p) => p.id === t.projectId && p.clientId === cid) &&
        isOpen(t),
    ).length;
  const projCountFor = (cid: string) =>
    data.projects.filter((p) => p.clientId === cid).length;

  const scopeProjectMatch = (p: VProject) =>
    (p.name + p.code).toLowerCase().includes(scopeQ.toLowerCase());
  const allMatchesScopeQ = "all projects".includes(scopeQ.toLowerCase());

  return (
    <div className={"portal app" + (flush ? " is-flush" : "")}>
      {navOpen ? (
        <div className="mobile-scrim" onClick={() => setNavOpen(false)} />
      ) : null}
      <aside className={"side" + (navOpen ? " is-open" : "")}>
        <div className="side-top">
          <Logo variant="reversed" width={158} />
          <div className="side-rule" />
        </div>

        <div className="org">
          <div className="org-label">
            {role === "admin" ? "Viewing client" : "Your account"}
          </div>
          <div className="org-name">{client?.name ?? "—"}</div>
          {role === "admin" ? (
            <div className="org-switch">
              <button
                className="org-trigger"
                onClick={() => setOrgOpen((o) => !o)}
              >
                <span>{client?.name ?? "Select client"}</span>
                <Icon name="chevrons-up-down" size={14} />
              </button>
            </div>
          ) : null}
        </div>

        {/* Project picker: admin only (clients see everything on their account). */}
        {role === "admin" ? (
          <div className="scope">
            <div className="org-label">{inProject ? "In project" : "Project"}</div>
            <button className="scope-trigger" onClick={() => setScopeOpen(true)}>
              <span className="scope-label">
                <span className="dot" />
                <span>{inProject ? projectName(pid) : "All projects"}</span>
              </span>
              <Icon name="chevrons-up-down" size={14} />
            </button>
            {inProject ? (
              <button className="scope-exit" onClick={exitProject}>
                <Icon name="arrow-left" size={13} />
                <span>Exit project</span>
              </button>
            ) : null}
          </div>
        ) : null}

        <nav className="nav">
          <div className="nav-sec">{inProject ? "This project" : "Portal"}</div>
          {navItems.map((n) => (
            <button
              key={n.id}
              className={"nav-item" + (page === n.id ? " is-on" : "")}
              onClick={() => {
                setPage(n.id);
                setNavOpen(false);
              }}
            >
              <Icon name={n.icon} size={16} />
              <span>{n.label}</span>
              {counts[n.id] ? (
                <span className="nav-count">{counts[n.id]}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="side-foot">
          <div className="who">
            <span className="who-av">{initials(me.name)}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="who-name">{me.name}</div>
              <div className="who-mail">{me.email}</div>
            </div>
            <IconButton
              icon="log-out"
              label="Sign out"
              variant="ghost"
              onClick={() => void logout()}
            />
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <button
            className="mobile-menu-btn"
            aria-label="Open menu"
            onClick={() => setNavOpen(true)}
          >
            <Icon name="folder-kanban" size={18} />
          </button>
          <div className="crumb">
            {[
              client?.name ?? "—",
              // Admin: client / scope / page; client: client / page.
              role === "admin" ? (inProject ? projectName(pid) : "All projects") : null,
              pageLabel(page),
              crumbTail || null,
            ]
              .filter(Boolean)
              .join(" / ")}
          </div>
          <div className="row-8">
            {role === "admin" ? <Badge tone="navy">Admin</Badge> : null}
            <span className="topbar-status">
              <Badge tone="teal" dot>
                All systems normal
              </Badge>
            </span>
          </div>
        </div>
        <div className="content">{renderPage()}</div>
      </div>

      {/* Client switcher — portalled to body (the sidebar is a sticky context). */}
      {orgOpen
        ? createPortal(
            <>
              <div
                className="org-scrim"
                onClick={() => {
                  setOrgOpen(false);
                  setOrgQ("");
                  setShowArchived(false);
                }}
              />
              <div className="org-pop">
                <div className="org-pop-head">
                  <div className="org-label" style={{ color: "var(--text-muted)" }}>
                    Switch client
                  </div>
                  <div className="org-pop-title">
                    Which client are you working on?
                  </div>
                </div>
                <div className="org-pop-search">
                  <Input
                    icon="search"
                    size="sm"
                    placeholder="Search clients"
                    value={orgQ}
                    onChange={(e) => setOrgQ(e.target.value)}
                  />
                </div>
                <div className="org-pop-list">
                  {data.clients
                    .filter((c) => clientMatch(c) && !c.archived)
                    .map((c) => (
                      <div key={c.id} className="org-opt-row">
                        <button
                          className={
                            "org-opt" + (c.id === clientId ? " is-on" : "")
                          }
                          style={{ flex: 1 }}
                          onClick={() => switchClient(c.id)}
                        >
                          <span className="org-opt-name">{c.name}</span>
                          <span className="org-opt-meta">
                            {projCountFor(c.id)} proj · {openCountFor(c.id)} open
                          </span>
                        </button>
                        <span className="org-archive">
                          <IconButton
                            icon="archive"
                            label={`Archive ${c.name}`}
                            variant="ghost"
                            onClick={() => void setClientArchived(c, true)}
                          />
                        </span>
                      </div>
                    ))}
                  {data.clients.filter((c) => clientMatch(c) && !c.archived)
                    .length === 0 ? (
                    <div className="org-pop-empty">
                      No client matches “{orgQ}”.
                    </div>
                  ) : null}
                  {showArchived
                    ? data.clients
                        .filter((c) => clientMatch(c) && c.archived)
                        .map((c) => (
                          <div key={c.id} className="org-opt-row">
                            <span
                              className="org-opt"
                              style={{ flex: 1, opacity: 0.55 }}
                            >
                              <span className="org-opt-name">{c.name}</span>
                            </span>
                            <IconButton
                              icon="archive-restore"
                              label={`Restore ${c.name}`}
                              variant="ghost"
                              onClick={() => void setClientArchived(c, false)}
                            />
                          </div>
                        ))
                    : null}
                  {data.clients.filter((c) => clientMatch(c) && c.archived)
                    .length ? (
                    <button
                      className="org-opt org-opt-muted"
                      onClick={() => setShowArchived((s) => !s)}
                    >
                      <span className="org-opt-name">
                        {showArchived
                          ? "Hide archived"
                          : `Show archived (${
                              data.clients.filter(
                                (c) => clientMatch(c) && c.archived,
                              ).length
                            })`}
                      </span>
                    </button>
                  ) : null}
                  <button
                    className="org-opt org-opt-new"
                    onClick={() => {
                      setOrgOpen(false);
                      setOrgQ("");
                      setShowArchived(false);
                      setClientCreateOpen(true);
                    }}
                  >
                    <span className="org-opt-name">+ New client</span>
                  </button>
                </div>
              </div>
            </>,
            document.body,
          )
        : null}

      {/* Project scope picker */}
      {scopeOpen
        ? createPortal(
            <>
              <div
                className="org-scrim"
                onClick={() => {
                  setScopeOpen(false);
                  setScopeQ("");
                }}
              />
              <div className="org-pop">
                <div className="org-pop-head">
                  <div className="org-label" style={{ color: "var(--text-muted)" }}>
                    Project scope
                  </div>
                  <div className="org-pop-title">
                    Which project are you looking at?
                  </div>
                </div>
                <div className="org-pop-search">
                  <Input
                    icon="search"
                    size="sm"
                    placeholder="Search projects"
                    value={scopeQ}
                    onChange={(e) => setScopeQ(e.target.value)}
                  />
                </div>
                <div className="org-pop-list">
                  {allMatchesScopeQ ? (
                    <button
                      className={"org-opt" + (pid === "all" ? " is-on" : "")}
                      onClick={exitProject}
                    >
                      <div className="opt-main">
                        <span className="org-opt-name">All projects</span>
                        <div className="opt-sub">Everything across this client</div>
                      </div>
                      <span className="org-opt-meta">
                        {clientProjects.length} proj
                      </span>
                    </button>
                  ) : null}
                  {clientProjects.filter(scopeProjectMatch).map((p) => (
                    <button
                      key={p.id}
                      className={"org-opt" + (pid === p.id ? " is-on" : "")}
                      onClick={() => enterProject(p.id)}
                    >
                      <div className="opt-main">
                        <span className="org-opt-name">{p.name}</span>
                        <div className="opt-sub">
                          {p.code} · {p.status}
                        </div>
                      </div>
                      <span className="org-opt-meta">
                        {
                          clientTickets.filter(
                            (t) => t.projectId === p.id && isOpen(t),
                          ).length
                        }{" "}
                        open
                      </span>
                    </button>
                  ))}
                  {clientProjects.filter(scopeProjectMatch).length === 0 &&
                  !allMatchesScopeQ ? (
                    <div className="org-pop-empty">
                      No project matches “{scopeQ}”.
                    </div>
                  ) : null}
                </div>
              </div>
            </>,
            document.body,
          )
        : null}

      {clientCreateOpen ? (
        <ClientCreateDialog
          open
          onClose={() => setClientCreateOpen(false)}
          onSaved={(id) => {
            data.reload();
            if (id) switchClient(id);
            toast("Client created.");
          }}
        />
      ) : null}

      {createPortal(
        <div className="toasts">
          {toasts.map((t) => (
            <Toast
              key={t.id}
              title={t.msg}
              tone="success"
              onClose={() =>
                setToasts((x) => x.filter((y) => y.id !== t.id))
              }
            />
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
